import React, { useEffect, useRef, useState } from "react";
import styles from "./otp-input.module.scss";

type OtpInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  resendSeconds?: number;
  onResend?: () => void;
  error?: string;
  autoFocus?: boolean;
};

const OtpInput = ({
  length = 6,
  value,
  onChange,
  onComplete,
  resendSeconds = 30,
  onResend,
  error,
  autoFocus = true,
}: OtpInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(resendSeconds);

  useEffect(() => {
    if (secondsLeft <= 0) return;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, length);
    onChange(digits);

    if (digits.length === length) {
      onComplete?.(digits);
    }
  };

  const handleResendClick = () => {
    if (secondsLeft > 0) return;
    onResend?.();
    setSecondsLeft(resendSeconds);
  };

  const activeIndex = Math.min(value.length, length - 1);

  return (
    <div className={styles.container}>
      <div
        className={styles.inputWrapper}
        onClick={() => inputRef.current?.focus()}
      >
        <div className={styles.boxes} aria-hidden="true">
          {Array.from({ length }).map((_, index) => (
            <div
              key={index}
              className={[
                styles.box,
                value[index] || (isFocused && index === activeIndex)
                  ? styles.boxActive
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {value[index] || ""}
            </div>
          ))}
        </div>

        <input
          ref={inputRef}
          className={styles.hiddenInput}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={length}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>

      {error ? <span className={styles.errorText}>{error}</span> : null}

      <div className={styles.resendRow}>
        <span className={styles.resendText}>
          Haven&apos;t received a code yet?{" "}
        </span>
        {secondsLeft > 0 ? (
          <span className={styles.resendMuted}>
            Resend in {secondsLeft} seconds.
          </span>
        ) : (
          <span
            className={styles.resendLink}
            role="button"
            tabIndex={0}
            onClick={handleResendClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleResendClick();
            }}
          >
            Resend
          </span>
        )}
      </div>
    </div>
  );
};

export default OtpInput;
